import React, { useState } from "react"
import {
  Box,
  Flex,
  HStack,
  MenuItemOption,
  MenuOptionGroup,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useHistory } from "react-router-dom"
import Menu from "components/dialogs/Menu"
import Button from "components/Buttons/Button"
import { DioboxEvent } from "sharedTypes"
import useCurrentAccount from "services/useCurrentAccount"
import AppleWalletPassPreview from "./AppleWalletPassPreview"
import { AppleIcon, GoogleIcon } from "./PassBuilderPlatformIcons"
import AppleWalletPassBackPreview from "./AppleWalletPassBackPreview"
import GoogleWalletPassPreview from "./GoogleWalletPassPreview"
import GoogleWalletPassBackPreview from "./GoogleWalletPassBackPreview"
import {
  walletPassDataToFormValues,
  resolvePassPreviewProps,
  formatEventAddress,
  formatEventDate,
  formatEventTime,
} from "./walletPassDataMappers"

type PassSide = "front" | "back"

const sideLabel: Record<PassSide, string> = {
  front: "Front of Pass",
  back: "Back of Pass",
}

type Props = {
  event: DioboxEvent
  builderPath: string
}

const WalletPassCustomization = ({ event, builderPath }: Props) => {
  const currentAccount = useCurrentAccount()
  const history = useHistory()
  const [side, setSide] = useState<PassSide>("front")
  const customizationEnabled = event.passBuilderEnabled

  const formValues = walletPassDataToFormValues(event.walletPassData)
  const {
    applePreviewProps, googlePreviewProps, googleDetailFields, backSections,
  } = resolvePassPreviewProps(
    formValues,
    {
      name: event.publicEventTitle || event.title,
      accountName: currentAccount.name,
      dateText: formatEventDate(event.startDate, event.timezone),
      timeText: formatEventTime(event.startDate, event.timezone),
      venue: event.venue,
      address: formatEventAddress(event),
    },
  )

  const applePreview = side === "back"
    ? (
      <AppleWalletPassBackPreview
        {...applePreviewProps}
        sections={backSections}
        eventTitle={event.publicEventTitle || event.title}
      />
    )
    : <AppleWalletPassPreview {...applePreviewProps} />
  const googlePreview = side === "back"
    ? (
      <GoogleWalletPassBackPreview
        {...googlePreviewProps}
        auxiliaryFields={googleDetailFields}
        sections={backSections}
      />
    )
    : <GoogleWalletPassPreview {...googlePreviewProps} />

  return (
    <Box borderWidth={1} borderColor="gray.200" bg="white" borderRadius="xl" overflow="hidden">
      <HStack
        px={3}
        py={1}
        bg="gray.50"
        borderBottomWidth={1}
        borderColor="gray.100"
        justify="space-between"
        spacing={4}
      >
        <Text px={3} fontWeight="600" color="gray.800">
          Preview
        </Text>

        <Menu
          noPadding
          placement="bottom-end"
          buttonText={sideLabel[side]}
          buttonVariant="ghost"
          buttonColor="black"
          menuItems={[
            <MenuOptionGroup
              key="side-options"
              defaultValue={side}
              type="radio"
              onChange={(value) => setSide(value as PassSide)}
            >
              {(Object.keys(sideLabel) as PassSide[]).map((value) => (
                <MenuItemOption key={value} value={value} py={3}>
                  {sideLabel[value]}
                </MenuItemOption>
              ))}
            </MenuOptionGroup>,
          ]}
        />
      </HStack>

      <Flex px={2} py={8} gap={5} align="flex-start" justify="center" bg="white" wrap="wrap">
        {(
          [
            {
              key: "apple", label: "Apple Wallet", icon: <AppleIcon variant="tab" />, preview: applePreview,
            },
            {
              key: "google", label: "Google Wallet", icon: <GoogleIcon variant="tab" />, preview: googlePreview,
            },
          ]
        ).map(({
          key, label, icon, preview,
        }) => (
          <Stack key={key} spacing={5} align="center" w="320px">
            <HStack spacing={2} h="34px">
              {icon}
              <Text fontWeight="600" color="gray.800">{label}</Text>
            </HStack>
            {preview}
          </Stack>
        ))}
      </Flex>

      <Flex py={6} justify="center" bg="white" borderTopWidth={1} borderColor="gray.200">
        <Button
          variant="outline"
          isDisabled={!customizationEnabled}
          onClick={() => history.push(builderPath)}
        >
          Customize Pass
        </Button>
      </Flex>
    </Box>
  )
}

export default WalletPassCustomization
